// @ts-check
/**
 * useLiveWidgets — periodic auto-refresh for pinned live widgets.
 *
 * Usage in a dashboard viewer:
 *
 *   const { data, refreshedAt, refreshNow } = useLiveWidgets(token, widgets, {
 *     intervalSeconds: 60,
 *     enabled: true,
 *   });
 *
 *   // `data[widgetId]` → {rows, columns, error?}
 *   // renderer picks fresh rows over the stamped chart_config.data.
 *
 * Behavior:
 *   • Only widgets with `chart_config.is_live && chart_config.structured_query.model_id`
 *     are included in the batch — inert widgets skip the round-trip entirely.
 *   • Runs once on mount, then every `intervalSeconds` seconds.
 *   • Pauses when the browser tab is hidden (Page Visibility API) so
 *     background tabs don't drain the connector.
 *   • `refreshNow()` triggers an immediate off-cycle refresh for
 *     manual buttons.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULT_INTERVAL = 60;
const MIN_INTERVAL = 10;

function pickLiveWidgets(widgets) {
  if (!Array.isArray(widgets)) return [];
  return widgets
    .map((w) => {
      const cc = w?.chart_config || {};
      const sq = cc?.structured_query;
      if (!cc.is_live || !sq || !sq.model_id) return null;
      return {
        widget_id: w.widget_id,
        model_id: sq.model_id,
        dimensions: sq.dimensions || [],
        measures: sq.measures || [],
        filters: sq.filters || [],
        limit: 100,
      };
    })
    .filter(Boolean);
}

export function useLiveWidgets(token, widgets, options = {}) {
  const intervalSeconds = Math.max(
    MIN_INTERVAL,
    Math.floor(options.intervalSeconds || DEFAULT_INTERVAL),
  );
  const enabled = options.enabled !== false;
  const [data, setData] = useState({});
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const liveWidgetsRef = useRef(pickLiveWidgets(widgets));

  useEffect(() => {
    liveWidgetsRef.current = pickLiveWidgets(widgets);
  }, [widgets]);

  const doRefresh = useCallback(async () => {
    const live = liveWidgetsRef.current;
    if (!token || !enabled || live.length === 0) return;
    // Cancel any in-flight refresh so we never render stale rows on
    // top of newer ones during a fast polling cycle.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (e) { /* ignore */ }
    }
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const res = await fetch(`${API}/api/embed/v2/widgets/live-data-batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ widgets: live }),
        signal: ac.signal,
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.message || `live-data-batch ${res.status}`);
      }
      const body = await res.json();
      const map = {};
      for (const row of body.results || []) {
        map[row.widget_id] = row;
      }
      setData(map);
      setRefreshedAt(body.refreshed_at || new Date().toISOString());
      setError(null);
    } catch (e) {
      if (e.name === 'AbortError') return;
      setError(e.message);
    }
  }, [token, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      // Skip the network hop when the tab is hidden. We still keep
      // the timer alive so the next visible tick can catch up.
      if (typeof document !== 'undefined' && document.hidden) return;
      await doRefresh();
    };
    tick();
    timer = setInterval(tick, intervalSeconds * 1000);
    // When the tab flips back to visible after being hidden, do an
    // immediate catch-up refresh so the numbers aren't stale.
    const onVis = () => {
      if (typeof document !== 'undefined' && !document.hidden) tick();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis);
    }
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (e) { /* ignore */ }
      }
    };
  }, [enabled, intervalSeconds, doRefresh]);

  return {
    data,
    refreshedAt,
    error,
    refreshNow: doRefresh,
    liveCount: liveWidgetsRef.current.length,
  };
}
